<template>
  <div class="r-select-otro">
    <f7-list-input
      type="select"
      :label="label"
      :value="valorSelect"
      floating-label
      outline
      :disabled="esReadonly"
      :class="{readonly: esReadonly}"
      @change="onSelect($event.target.value)"
    >
      <option value=""></option>
      <option v-for="it in itemsNormalizados" :key="'opt-' + it.valor" :value="it.valor">{{ it.nombre }}</option>
      <option :value="valorOtro">{{ labelOtro }}</option>
    </f7-list-input>
    <r-input
      v-if="modoOtro"
      ref="textoOtro"
      floating-label
      outline
      type="text"
      :label="labelTextoCalculado"
      :form="form"
      :name="name"
      :clearButton="true"
      :readonly="readonly"
      :overrideReadonly="overrideReadonly"
      :viewTooltip="viewTooltip"
    ></r-input>
  </div>
</template>
<style scoped>
.r-select-otro {
  display: flex;
  flex-direction: column;
  flex: 1;
}
</style>
<script>
import rInput from "./rInput.vue";

/**
 * Desplegable con valores fijos más una opción «OTRA» que pide el valor escrito a mano.
 *
 * El campo del formulario (`name`) guarda SIEMPRE el valor final: uno de los fijos o el texto
 * libre. «OTRA» no se guarda nunca: es solo el estado del desplegable que abre la caja de texto.
 * Al cargar, si el valor guardado no es ninguno de los fijos, se entra en modo «otra» con ese
 * texto. Lo usan la ficha del pedido (reglamento de la línea) y el cambio masivo de los listados
 * (columnas SELECT con `cambioMasivo.opciones.valorOtro`).
 */
export default {
  name: "r-select-otro",
  components: {
    rInput
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      default: ""
    },
    /** Lista de valores fijos: cadenas u objetos ({nombre, valor} por defecto; ver idProp/labelProp). */
    items: {
      type: Array,
      default: () => []
    },
    idProp: {
      type: String,
      default: "valor"
    },
    labelProp: {
      type: String,
      default: "nombre"
    },
    /** Valor interno de la opción que abre el texto libre. No se guarda en el formulario. */
    valorOtro: {
      type: String,
      default: "OTRA"
    },
    labelOtro: {
      type: String,
      default: "OTRA"
    },
    /** Etiqueta de la caja de texto; por defecto la del desplegable + " (otra)". */
    labelTexto: {
      type: String,
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    },
    overrideReadonly: {
      type: Boolean,
      default: false
    },
    viewTooltip: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      modoOtro: false
    };
  },
  computed: {
    itemsNormalizados() {
      const self = this;
      return (self.items || []).map(it => {
        if (it === null || typeof it !== "object") {
          return { valor: it == null ? "" : String(it), nombre: it == null ? "" : String(it) };
        }
        const valor = it[self.idProp];
        const nombre = it[self.labelProp];
        return { valor: valor == null ? "" : String(valor), nombre: nombre == null ? String(valor) : String(nombre) };
      });
    },
    valorActual() {
      const self = this;
      const v = self.form && self.form.formData ? self.leerValor() : undefined;
      return v === undefined || v === null ? "" : String(v);
    },
    valorSelect() {
      const self = this;
      if (self.modoOtro) {
        return self.valorOtro;
      }
      return self.valorActual;
    },
    labelTextoCalculado() {
      const self = this;
      return self.labelTexto || ((self.label || "") + " (" + self.labelOtro.toLowerCase() + ")");
    },
    esReadonly() {
      const self = this;
      if (self.overrideReadonly) {
        return !!self.readonly;
      }
      return !!self.readonly || !!(self.form && self.form.readonly);
    }
  },
  watch: {
    valorActual: {
      immediate: true,
      handler(nuevo) {
        const self = this;
        if (nuevo && !self.esFijo(nuevo)) {
          self.modoOtro = true;
        } else if (nuevo && self.esFijo(nuevo)) {
          self.modoOtro = false;
        }
        // Vacío: se deja el modo como esté (si se acaba de elegir «OTRA», la caja sigue abierta).
      }
    }
  },
  methods: {
    esFijo(valor) {
      const self = this;
      return self.itemsNormalizados.some(it => it.valor === valor);
    },
    /** Misma firma que r-input/r-autocomplete: rListFilterItem lo llama al limpiar el campo. */
    setValue(valor) {
      const self = this;
      const v = valor === undefined || valor === null || valor === "" ? null : String(valor);
      self.modoOtro = !!v && !self.esFijo(v);
      self.escribirValor(v);
    },
    onSelect(valor) {
      const self = this;
      if (valor === self.valorOtro) {
        self.modoOtro = true;
        self.escribirValor(null);
        self.$nextTick(() => {
          const input = self.$refs.textoOtro && self.$refs.textoOtro.$el ? self.$refs.textoOtro.$el.querySelector("input") : null;
          if (input) {
            input.focus();
          }
        });
      } else {
        self.modoOtro = false;
        self.escribirValor(valor || null);
      }
    },
    leerValor() {
      const self = this;
      const namespaces = self.name.split(".");
      let context = self.form.formData;
      for (let i = 0; i < namespaces.length; i++) {
        if (context === undefined || context === null) {
          return undefined;
        }
        context = context[namespaces[i]];
      }
      return context;
    },
    /** Misma mecánica que r-input: crea los tramos intermedios que falten y escribe con $set para que sea reactivo. */
    escribirValor(valor) {
      const self = this;
      const namespaces = self.name.split(".");
      let context = self.form.formData;
      for (let i = 0; i < namespaces.length - 1; i++) {
        const n = namespaces[i];
        if (context[n] === undefined || context[n] === null) {
          const siguiente = namespaces[i + 1];
          self.$set(context, n, /^\d+$/.test(siguiente) ? [] : {});
        }
        context = context[n];
      }
      self.$set(context, namespaces[namespaces.length - 1], valor);
      self.$emit("change", valor, self);
      if (self.form && self.form.$emit) {
        self.form.$emit("change", self.name, valor);
      }
    }
  }
};
</script>
